import { Controller } from "@hotwired/stimulus"

// Two modes, selected by the trigger value:
//   hover (default) — transient label bubble; skipped on touch screens.
//   click — dismissable popover (outside click / Escape); works on touch.
export default class extends Controller {
  static values = {
    text: String,
    position: { type: String, default: "top" },
    trigger: { type: String, default: "hover" }
  }

  connect() {
    this._tooltip = null
    this._showBound = this._show.bind(this)
    this._hideBound = this._hide.bind(this)
    this._toggleBound = this._toggle.bind(this)
    this._outsideBound = this._outside.bind(this)
    this._keydownBound = this._keydown.bind(this)

    if (this.clickMode) {
      this.element.addEventListener("click", this._toggleBound)
      this.element.setAttribute("aria-expanded", "false")
    } else {
      this.element.addEventListener("mouseenter", this._showBound)
      this.element.addEventListener("mouseleave", this._hideBound)
      this.element.addEventListener("focus", this._showBound)
      this.element.addEventListener("blur", this._hideBound)
    }

    if (!this.element.getAttribute("aria-label")) {
      this.element.setAttribute("aria-label", this.textValue)
    }
  }

  disconnect() {
    this._hide()
    if (this.clickMode) {
      this.element.removeEventListener("click", this._toggleBound)
    } else {
      this.element.removeEventListener("mouseenter", this._showBound)
      this.element.removeEventListener("mouseleave", this._hideBound)
      this.element.removeEventListener("focus", this._showBound)
      this.element.removeEventListener("blur", this._hideBound)
    }
  }

  get clickMode() {
    return this.triggerValue === "click"
  }

  _toggle(event) {
    event.preventDefault()
    this._tooltip ? this._hide() : this._show()
  }

  _show() {
    if (this._tooltip) return
    if (!this.clickMode && window.matchMedia("(hover: none)").matches) return

    this._tooltip = document.createElement("div")
    this._tooltip.className = this.clickMode ? "tooltip-bubble tooltip-bubble--popover" : "tooltip-bubble"
    this._tooltip.textContent = this.textValue
    this._tooltip.setAttribute("role", "tooltip")
    document.body.appendChild(this._tooltip)
    this._position()

    if (this.clickMode) {
      this.element.setAttribute("aria-expanded", "true")
      // Capture phase so a click that re-renders/stops propagation elsewhere
      // still closes the popover.
      document.addEventListener("pointerdown", this._outsideBound, true)
      document.addEventListener("keydown", this._keydownBound)
    }
  }

  _hide() {
    if (!this._tooltip) return
    this._tooltip.remove()
    this._tooltip = null
    if (this.clickMode) {
      this.element.setAttribute("aria-expanded", "false")
      document.removeEventListener("pointerdown", this._outsideBound, true)
      document.removeEventListener("keydown", this._keydownBound)
    }
  }

  _outside(event) {
    if (this.element.contains(event.target)) return
    if (this._tooltip && this._tooltip.contains(event.target)) return
    this._hide()
  }

  _keydown(event) {
    if (event.key !== "Escape") return
    this._hide()
    this.element.focus()
  }

  _position() {
    if (!this._tooltip) return

    const rect = this.element.getBoundingClientRect()
    const tip = this._tooltip.getBoundingClientRect()
    const gap = 6

    let top, left

    switch (this.positionValue) {
      case "bottom":
        top = rect.bottom + gap
        left = rect.left + (rect.width - tip.width) / 2
        break
      case "left":
        top = rect.top + (rect.height - tip.height) / 2
        left = rect.left - tip.width - gap
        break
      case "right":
        top = rect.top + (rect.height - tip.height) / 2
        left = rect.right + gap
        break
      default:
        top = rect.top - tip.height - gap
        left = rect.left + (rect.width - tip.width) / 2
    }

    left = Math.max(8, Math.min(left, window.innerWidth - tip.width - 8))
    top = Math.max(8, top)

    this._tooltip.style.top = `${top}px`
    this._tooltip.style.left = `${left}px`
  }
}
